import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Building2, Save, AlertCircle, CheckCircle, 
  MapPin, Mail, Phone, Hash, FileText, Receipt, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Indian states for dropdown
const INDIAN_STATES = [
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Arunachal Pradesh', code: '12' },
  { name: 'Assam', code: '18' },
  { name: 'Bihar', code: '10' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Delhi', code: '07' },
  { name: 'Goa', code: '30' },
  { name: 'Gujarat', code: '24' },
  { name: 'Haryana', code: '06' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Karnataka', code: '29' },
  { name: 'Kerala', code: '32' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Manipur', code: '14' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Mizoram', code: '15' },
  { name: 'Nagaland', code: '13' },
  { name: 'Odisha', code: '21' },
  { name: 'Punjab', code: '03' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Sikkim', code: '11' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Telangana', code: '36' },
  { name: 'Tripura', code: '16' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'West Bengal', code: '19' },
  { name: 'Jammu and Kashmir', code: '01' },
  { name: 'Ladakh', code: '38' },
  { name: 'Chandigarh', code: '04' },
  { name: 'Puducherry', code: '34' },
  { name: 'Andaman and Nicobar Islands', code: '35' },
  { name: 'Dadra and Nagar Haveli', code: '26' },
  { name: 'Lakshadweep', code: '31' },
];

const BillingProfileSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  
  const [formData, setFormData] = useState({
    legal_name: '',
    billing_address: '',
    state: 'Telangana',
    email: '',
    gstin: '',
    pan: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBillingProfile();
  }, []);

  const fetchBillingProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/invoice/billing-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        setFormData({
          legal_name: response.data.legal_name || '',
          billing_address: response.data.billing_address || '',
          state: response.data.state || 'Telangana',
          email: response.data.email || '',
          gstin: response.data.gstin || '',
          pan: response.data.pan || '',
          phone: response.data.phone || ''
        });
        setHasExisting(true);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // No billing profile yet - try to pre-fill from user data
        try {
          const token = localStorage.getItem('token');
          const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userResponse.data?.email) {
            setFormData(prev => ({
              ...prev,
              email: userResponse.data.email
            }));
          }
        } catch (e) {
          console.error('Error fetching user data:', e);
        }
        setHasExisting(false);
      } else {
        console.error('Error fetching billing profile:', err);
        toast.error('Failed to load billing profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateGSTIN = (gstin) => {
    if (!gstin) return true; // Optional
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return pattern.test(gstin.toUpperCase());
  };

  const validatePAN = (pan) => {
    if (!pan) return true; // Optional
    const pattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return pattern.test(pan.toUpperCase());
  };

  const validateEmail = (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal/Business name is required';
    }
    
    if (!formData.billing_address.trim()) {
      newErrors.billing_address = 'Billing address is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required for GST calculation';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required for invoice delivery';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.gstin && !validateGSTIN(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format (e.g., 36AABCT1234A1Z5)';
    }
    
    if (formData.pan && !validatePAN(formData.pan)) {
      newErrors.pan = 'Invalid PAN format (e.g., AABCT1234A)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        legal_name: formData.legal_name.trim(),
        billing_address: formData.billing_address.trim(),
        state: formData.state,
        email: formData.email.trim().toLowerCase(),
        gstin: formData.gstin.trim().toUpperCase() || null,
        pan: formData.pan.trim().toUpperCase() || null,
        phone: formData.phone.trim() || null
      };
      
      if (hasExisting) {
        // Update existing profile
        await axios.put(`${API_URL}/api/invoice/billing-profile`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Create new profile
        await axios.post(`${API_URL}/api/invoice/billing-profile`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      setHasExisting(true);
      toast.success('Billing profile saved successfully!');
    } catch (err) {
      console.error('Error saving billing profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to save billing profile');
    } finally {
      setSaving(false);
    }
  };

  const getStateCode = (stateName) => {
    const state = INDIAN_STATES.find(s => s.name === stateName);
    return state ? state.code : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Receipt className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Billing Profile</h1>
                  <p className="text-sm text-gray-500">Configure billing details for GST invoices</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {hasExisting && (
                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Profile Saved
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-8 px-4">
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">GST Invoice Details</p>
              <p>Your billing details will appear on all GST invoices generated for wallet recharge. 
                 The state you select determines whether CGST+SGST or IGST applies to your transactions.</p>
            </div>
          </div>
        </div>

        {/* GST Explainer */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">How GST Works</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Same State</strong> (e.g., Telangana to Telangana): CGST 9% + SGST 9% = 18%</li>
                <li><strong>Different State</strong> (e.g., Telangana to Karnataka): IGST 18%</li>
                <li>Total GST rate is always 18%, but the breakdown differs based on location</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-gray-600" />
                Business Information
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal/Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="legal_name"
                  value={formData.legal_name}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC Law Associates"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.legal_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="legal-name-input"
                />
                {errors.legal_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.legal_name}</p>
                )}
              </div>

              {/* Billing Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    name="billing_address"
                    value={formData.billing_address}
                    onChange={handleInputChange}
                    placeholder="Full billing address for invoices"
                    rows={3}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.billing_address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    data-testid="billing-address-input"
                  />
                </div>
                {errors.billing_address && (
                  <p className="mt-1 text-sm text-red-600">{errors.billing_address}</p>
                )}
              </div>

              {/* State Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="state-select"
                >
                  {INDIAN_STATES.map(state => (
                    <option key={state.code} value={state.name}>
                      {state.name} ({state.code})
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  State Code: {getStateCode(formData.state)} (determines GST type)
                </p>
              </div>
            </div>
          </div>

          {/* Tax Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-gray-600" />
                Tax Information (Optional)
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                If you have a GSTIN, you may be eligible for Input Tax Credit
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* GSTIN and PAN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GSTIN
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleInputChange}
                      placeholder="e.g., 36AABCA1234B1Z6"
                      maxLength={15}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                        errors.gstin ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="gstin-input"
                    />
                  </div>
                  {errors.gstin && (
                    <p className="mt-1 text-sm text-red-600">{errors.gstin}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">15-character GST Identification Number</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAN
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="pan"
                      value={formData.pan}
                      onChange={handleInputChange}
                      placeholder="e.g., AABCA1234B"
                      maxLength={10}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                        errors.pan ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="pan-input"
                    />
                  </div>
                  {errors.pan && (
                    <p className="mt-1 text-sm text-red-600">{errors.pan}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">10-character Permanent Account Number</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-gray-600" />
                Contact Information
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="billing@example.com"
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="email-input"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Invoices will be sent to this email</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g., 9876543210"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-button"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {hasExisting ? 'Update Profile' : 'Save Profile'}
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default BillingProfileSettings;
